import clsx from "clsx";
import { formatCurrency } from "@/lib/documentCalc";
import type { AppAngebotBundle, AppAngebotPosition } from "@/lib/projectOverview";
import { formatHours } from "@/pages/projekte/hoursUsage";
import { KalkStundenOverview } from "@/projectOverview/KalkStundenOverview";

function preisartLabel(value: string | null | undefined): string | null {
  if (value === "nachweis") return "Nachweis";
  if (value === "festpreis") return "Festpreis";
  return null;
}

function PositionHours({
  position,
  includePrices,
}: {
  position: AppAngebotPosition;
  includePrices: boolean;
}) {
  const lohn = Array.isArray(position.lohn_liste) ? position.lohn_liste : [];
  if (lohn.length) {
    return (
      <ul className="mt-1 space-y-0.5 text-xs text-label-secondary">
        {lohn.map((zeile, idx) => (
          <li key={`${zeile.lohnart_id || zeile.bezeichnung || "lohn"}-${idx}`}>
            {zeile.bezeichnung || zeile.lohnart_id || "Stunden"}: {formatHours(zeile.stunden)}
            {zeile.preisbezug === "gesamt" ? " gesamt" : ""}
            {includePrices && zeile.stundensatz_netto != null
              ? ` · ${formatCurrency(zeile.stundensatz_netto)}/h`
              : null}
          </li>
        ))}
      </ul>
    );
  }
  if (position.preisart === "nachweis" && position.nachweis_stunden != null && position.nachweis_stunden > 0) {
    return (
      <p className="mt-1 text-xs text-label-secondary">
        Angebotene Nachweisstunden: {formatHours(position.nachweis_stunden)}
      </p>
    );
  }
  if (position.stunden != null && position.stunden > 0) {
    return (
      <p className="mt-1 text-xs text-label-secondary">Stunden: {formatHours(position.stunden)}</p>
    );
  }
  return null;
}

function PositionRow({
  position,
  includePrices,
}: {
  position: AppAngebotPosition;
  includePrices: boolean;
}) {
  const typ = String(position.position_typ || "").toLowerCase();
  if (typ === "titel") {
    return (
      <li className="border-b border-separator py-2 last:border-b-0">
        <p className="text-sm font-semibold text-label">{position.bezeichnung || "Titel"}</p>
      </li>
    );
  }
  if (typ === "text" || typ === "hinweis") {
    return (
      <li className="border-b border-separator py-2 last:border-b-0">
        <p className="text-sm text-label-secondary whitespace-pre-wrap">
          {position.bezeichnung || position.beschreibung || "—"}
        </p>
      </li>
    );
  }

  const art = preisartLabel(position.preisart);
  const mengeLabel =
    position.menge != null
      ? `${String(position.menge).replace(".", ",")}${position.einheit ? ` ${position.einheit}` : ""}`
      : null;

  return (
    <li className="border-b border-separator py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-label">{position.bezeichnung || "—"}</p>
          {position.beschreibung ? (
            <p className="mt-0.5 text-xs text-label-secondary whitespace-pre-wrap">
              {position.beschreibung}
            </p>
          ) : null}
          <div className="mt-1 flex flex-wrap gap-1.5">
            {art ? (
              <span
                className={clsx(
                  "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  art === "Nachweis"
                    ? "bg-status-orange/15 text-status-orange"
                    : "bg-status-blue/15 text-status-blue",
                )}
              >
                {art}
              </span>
            ) : null}
            {mengeLabel ? (
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                {mengeLabel}
              </span>
            ) : null}
          </div>
          <PositionHours position={position} includePrices={includePrices} />
        </div>
        {includePrices && position.gesamt_netto != null ? (
          <p className="shrink-0 text-sm font-semibold tabular-nums text-label">
            {formatCurrency(position.gesamt_netto)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

export function OfferPositionsView({
  bundle,
  emptyText = "Kein App-Angebot für dieses Projekt.",
  showKalkStunden = true,
  kalkStundenLabel = "Kalkulierte Festpreis-Stunden",
  showNachweisStunden = true,
}: {
  bundle: AppAngebotBundle | null;
  emptyText?: string;
  /** Pro-Angebot im Modal oft aus, wenn Stunden oben aggregiert stehen. */
  showKalkStunden?: boolean;
  kalkStundenLabel?: string;
  showNachweisStunden?: boolean;
}) {
  if (!bundle?.dokument_id) {
    return <p className="text-sm text-label-secondary">{emptyText}</p>;
  }

  const positions = Array.isArray(bundle.positionen) ? bundle.positionen : [];
  const kalkStunden = Array.isArray(bundle.kalk_stunden) ? bundle.kalk_stunden : [];
  const includePrices = Boolean(bundle.include_prices);
  // Einleitung/Schluss nur für Büro (API liefert sie Monteuren bereits nicht).
  const einleitung = includePrices ? String(bundle.einleitung || "").trim() : "";
  const schlusstext = includePrices ? String(bundle.schlusstext || "").trim() : "";
  const nachweisStunden =
    bundle.nachweis_stunden != null && Number(bundle.nachweis_stunden) > 0
      ? Number(bundle.nachweis_stunden)
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-label">
            {bundle.dokument_nummer || "Angebot (Entwurf)"}
          </p>
          {bundle.betreff ? (
            <p className="text-xs text-label-secondary">{bundle.betreff}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {bundle.ist_beauftragt ? (
            <span className="rounded-full bg-status-green/15 px-2.5 py-0.5 text-xs font-semibold text-status-green">
              Beauftragt
            </span>
          ) : null}
          {bundle.status ? (
            <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-semibold text-label-secondary">
              {bundle.status}
            </span>
          ) : null}
        </div>
      </div>

      {showKalkStunden && kalkStunden.length > 0 ? (
        <KalkStundenOverview items={kalkStunden} title={kalkStundenLabel} />
      ) : null}

      {showNachweisStunden && nachweisStunden != null ? (
        <div className="flex justify-between gap-3 text-sm text-label">
          <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Angebotene Nachweisstunden
          </span>
          <span className="tabular-nums font-medium">{formatHours(nachweisStunden)}</span>
        </div>
      ) : null}

      {einleitung ? (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Einleitung
          </p>
          <p className="text-sm text-label whitespace-pre-wrap">{einleitung}</p>
        </div>
      ) : null}

      {positions.length ? (
        <ul className="rounded-ios border border-separator bg-card/40 px-3">
          {positions.map((position, idx) => (
            <PositionRow
              key={position.id ?? `${position.sort_index ?? idx}-${position.bezeichnung}`}
              position={position}
              includePrices={includePrices}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-label-secondary">Keine Positionen vorhanden.</p>
      )}

      {schlusstext ? (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Schlusstext
          </p>
          <p className="text-sm text-label whitespace-pre-wrap">{schlusstext}</p>
        </div>
      ) : null}

      {includePrices && bundle.summe_brutto != null ? (
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
          <dt className="text-label-secondary">Summe netto</dt>
          <dd className="text-right tabular-nums">{formatCurrency(bundle.summe_netto)}</dd>
          <dt className="text-label-secondary">MwSt.</dt>
          <dd className="text-right tabular-nums">{formatCurrency(bundle.summe_steuer)}</dd>
          <dt className="font-semibold text-label">Summe brutto</dt>
          <dd className="text-right font-semibold tabular-nums">
            {formatCurrency(bundle.summe_brutto)}
          </dd>
        </dl>
      ) : null}
    </div>
  );
}
